/** Read-only grid of the bookings in a package (all bookings sharing a
 *  non-empty package_id). The current booking's row is highlighted; every other
 *  row opens its own booking (the open column, same tab) and the Start cell
 *  offers the run's manifest. */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Tooltip } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { PackageBooking } from '@/api/bookings';
import { act, utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';

import { departureActions, type BookingCtx } from './bookingLinks';
import { EmptyLine, READONLY_GRID_PROPS, READONLY_GRID_SX, TagsCell, TourLink, UserLink } from './common';

function buildColumns(ctx: BookingCtx): GridColDef<PackageBooking>[] {
  return [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      renderCell: (p) =>
        p.row.is_current ? null : (
          <Tooltip title={`Open booking #${p.row.id}`}>
            <IconButton
              size="small"
              component={RouterLink}
              to={`/bookings/${p.row.id}`}
              aria-label={`Open booking #${p.row.id}`}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    },
    {
      field: 'tour',
      headerName: 'Tour',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (p) => <TourLink tour={p.row.tour} ctx={ctx} />,
    },
    {
      field: 'start_ts',
      headerName: 'Start',
      width: 180,
      renderCell: (p) => {
        const when = p.row.start_ts ?? p.row.date;
        if (!when) return '—';
        return (
          <ValueActions
            title={p.row.start_ts ? utcTitle(p.row.start_ts) : undefined}
            actions={[
              ...(p.row.is_current ? [] : [act.open(`/bookings/${p.row.id}`, 'Open booking')]),
              ...departureActions(p.row, ctx),
            ]}
          >
            {p.row.start_ts ? formatDate(p.row.start_ts) : when}
          </ValueActions>
        );
      },
    },
    {
      field: 'user',
      headerName: 'Guest',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (p) => <UserLink user={p.row.user} ctx={ctx} />,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (p) => <TagsCell tags={p.row.tags} ctx={ctx} />,
    },
  ];
}

export function PackageBookingsGrid({
  bookings,
  ctx,
}: {
  bookings: PackageBooking[];
  ctx: BookingCtx;
}) {
  const columns = useMemo(() => buildColumns(ctx), [ctx]);
  if (!bookings.length) return <EmptyLine>No package bookings.</EmptyLine>;
  return (
    <DataGridPro
      rows={bookings}
      columns={columns}
      getRowId={(r) => r.id}
      getRowClassName={(p) => (p.row.is_current ? 'current-booking' : '')}
      sx={[READONLY_GRID_SX, { '& .current-booking': { bgcolor: 'action.selected' } }]}
      {...READONLY_GRID_PROPS}
    />
  );
}
